import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { type ClarityLinkCodeStepProps } from '@src/components/sco/Advisors/clarityLink/ClarityLinkCodeStep';
import { CLARITY_SUPPORT_EMAIL, clarityLinkErrorMessage } from '@src/components/sco/Advisors/clarityLink/clarityLinkHelpers';
import { STANDALONE_ADVISORS_PATHS } from '@src/components/sco/Advisors/paths';
import { type ClarityAdvisorImportHoldPhase } from '@src/components/sco/Advisors/sections/clarityAdvisorImportHold';
import { adviseTopicsToCategoryKeys } from '@src/components/sco/Advisors/utils/categoryKeys';
import { CLAIM_CLARITY_ADVISOR_IMPORT } from '@src/gql/mutations/ClaimClarityAdvisorImport';
import { REQUEST_CLARITY_LINK_CODE } from '@src/gql/mutations/ClarityLink';
import { EXTRACT_ADVISOR_APPLICATION_FROM_BIO } from '@src/gql/mutations/ExtractAdvisorApplicationFromBio';
import { UPSERT_ADVISOR_PROFILE } from '@src/gql/mutations/UpsertAdvisorProfile';
import { ADVISOR_APPLY_USER_PROFILE } from '@src/gql/queries/AdvisorApplyUserProfile';
import { CLARITY_ADVISOR_IMPORT } from '@src/gql/queries/ClarityAdvisorImport';
import { MY_ADVISOR_PROFILE } from '@src/gql/queries/MyAdvisorProfile';
import { ClaimClarityAdvisorImportInput, ExtractAdvisorApplicationFromBioInput, Mutation, Query, ScoClarityAdvisorImportClaimStatus, ScoClarityLinkProofMethod, UpsertAdvisorProfileInput } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { prettyErrorMessage } from '@src/lib/mutationErrors';
import { startupsPath } from '@src/lib/startups-url';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
export type { ClarityAdvisorImportHoldPhase };

/** Clarity link plan (D2, D3): what the on-ramp shows instead of, or next to, the confirm button. */
export type ClarityAdvisorImportProof = {
  /** Why this profile can't be imported by this member (held by someone else, admin only). */
  blockedMessage?: string | null;
  /** Shown under the confirm button when an emailed code will be needed. */
  hint?: string | null;
  /** The code step, once a code was sent. Replaces the confirm button. */
  codeStep?: ClarityLinkCodeStepProps | null;
};
const emptyList = (value?: Array<string | null> | null) => !value?.filter(Boolean).length;
export const useClarityAdvisorImport = (handle: string) => {
  const router = useRouter();
  const apolloClient = useApolloClient();
  const {
    currentUser,
    currentUserLoading,
    refetchCurrentUser
  } = useCurrentUser();
  const isRegistered = !!currentUser?.isRegistered;
  const [error, setError] = useState<string | null>(null);
  const [holdPhase, setHoldPhase] = useState<ClarityAdvisorImportHoldPhase>('idle');
  const [codeSent, setCodeSent] = useState<{
    maskedEmail: string | null;
  } | null>(null);
  const [code, setCode] = useState('');
  const importQuery = useQuery<Pick<Query, 'clarityAdvisorImport'>, {
    handle: string;
  }>(CLARITY_ADVISOR_IMPORT, {
    skip: !handle,
    variables: {
      handle
    }
  });
  const [claimImport, claimMutation] = useMutation<Pick<Mutation, 'claimClarityAdvisorImport'>, {
    input: ClaimClarityAdvisorImportInput;
  }>(CLAIM_CLARITY_ADVISOR_IMPORT);
  const [requestCode, requestCodeMutation] = useMutation<Pick<Mutation, 'requestClarityLinkCode'>>(REQUEST_CLARITY_LINK_CODE);
  const [extractApplication, extractMutation] = useMutation<Pick<Mutation, 'extractAdvisorApplicationFromBio'>, {
    input: ExtractAdvisorApplicationFromBioInput;
  }>(EXTRACT_ADVISOR_APPLICATION_FROM_BIO);
  const [upsertProfile] = useMutation<Pick<Mutation, 'upsertAdvisorProfile'>, {
    input: UpsertAdvisorProfileInput;
  }>(UPSERT_ADVISOR_PROFILE);
  const preview = importQuery.data?.clarityAdvisorImport;
  const claimStatus = preview?.claimStatus || ScoClarityAdvisorImportClaimStatus.NotFound;
  const proofMethod = preview?.proofMethod || null;
  const expert = preview?.expert || null;
  const canonicalScreenName = preview?.canonicalScreenName || null;
  const firstName = expert?.firstName?.trim() || null;
  const displayName = expert?.fullName?.trim() || firstName || handle;
  const applyHref = STANDALONE_ADVISORS_PATHS.apply;
  const becomeHref = STANDALONE_ADVISORS_PATHS.becomeAStartupAdvisor;
  const importHref = handle ? STANDALONE_ADVISORS_PATHS.becomeAnAdvisorClarity(handle) : becomeHref;
  const loginHref = startupsPath('/login', {
    returnTo: importHref
  });
  // An unknown or non-expert handle has nothing to import: callers fall back to the generic CTA (#29).
  const hasImportableExpert = !!preview && claimStatus !== ScoClarityAdvisorImportClaimStatus.NotFound && claimStatus !== ScoClarityAdvisorImportClaimStatus.NotExpert;
  const importCtaLabel = displayName ? `Import ${displayName} from Clarity.fm` : 'Import from Clarity.fm';
  const runClaim = useCallback(async (withCode: string | null) => {
    if (!handle) {
      return;
    }
    setError(null);
    setHoldPhase('claiming');
    try {
      const claimed = await claimImport({
        variables: {
          input: {
            handle,
            confirm: true,
            code: withCode
          }
        }
      });
      setCodeSent(null);
      const payload = claimed.data?.claimClarityAdvisorImport;
      const profile = payload?.advisorProfile;
      const bio = preview?.expert?.bio?.trim();
      if (bio) {
        setHoldPhase('extracting');
        const extracted = await extractApplication({
          variables: {
            input: {
              bio
            }
          }
        });
        const extract = extracted.data?.extractAdvisorApplicationFromBio;
        const categoryKeys = adviseTopicsToCategoryKeys(extract?.categories || []);
        const skillTagTitles = (extract?.skills || []).filter((title): title is string => !!title);
        const input: UpsertAdvisorProfileInput = {};
        if (!profile?.headline && extract?.headline) {
          input.headline = extract.headline;
        }
        if (emptyList(profile?.categoryKeys) && categoryKeys.length) {
          input.categoryKeys = categoryKeys;
        }
        if (emptyList(profile?.skillTagTitles) && skillTagTitles.length) {
          input.skillTagTitles = skillTagTitles;
        }
        if (Object.keys(input).length) {
          setHoldPhase('saving');
          await upsertProfile({
            variables: {
              input
            }
          });
        }
      }
      setHoldPhase('redirecting');
      try {
        await Promise.all([refetchCurrentUser(), apolloClient.query({
          query: MY_ADVISOR_PROFILE,
          fetchPolicy: 'network-only'
        }), apolloClient.query({
          query: ADVISOR_APPLY_USER_PROFILE,
          fetchPolicy: 'network-only'
        })]);
      } catch {
        // Apply still network-fetches; a warm-cache miss should not block the redirect.
      }
      await router.push(applyHref);
    } catch (err) {
      setHoldPhase('idle');
      setError(clarityLinkErrorMessage(err, prettyErrorMessage(err) || 'Could not import that Clarity profile.'));
    }
  }, [apolloClient, applyHref, claimImport, extractApplication, handle, preview?.expert?.bio, refetchCurrentUser, router, upsertProfile]);

  // D2/D3: email a code to the Clarity address unless the confirmed emails already match.
  const sendCode = useCallback(async () => {
    if (!handle) {
      return;
    }
    setError(null);
    try {
      const result = await requestCode({
        variables: {
          input: {
            handle
          }
        }
      });
      const payload_0 = result.data?.requestClarityLinkCode;
      if (payload_0?.proofMethod === ScoClarityLinkProofMethod.EmailMatch) {
        await runClaim(null);
        return;
      }
      setCode('');
      setCodeSent({
        maskedEmail: payload_0?.maskedEmail || null
      });
    } catch (err_0) {
      setError(clarityLinkErrorMessage(err_0, 'Could not send a code.'));
    }
  }, [handle, requestCode, runClaim]);
  const onConfirm = useCallback(() => proofMethod === ScoClarityLinkProofMethod.EmailCode ? sendCode() : runClaim(null), [proofMethod, runClaim, sendCode]);
  const loading = !!handle && (currentUserLoading || importQuery.loading && !preview);
  // D4: a profile another account holds can't be imported; D2: admin-only profiles can't either.
  const blockedMessage = !isRegistered ? null : claimStatus === ScoClarityAdvisorImportClaimStatus.ClaimedByOther ? `This Clarity profile is linked to another Startups.com account. Contact ${CLARITY_SUPPORT_EMAIL} if it's yours.` : proofMethod === ScoClarityLinkProofMethod.AdminOnly ? `We can't verify this Clarity profile by email. Contact ${CLARITY_SUPPORT_EMAIL} to import it.` : null;
  const canConfirm = isRegistered && !blockedMessage && claimStatus !== ScoClarityAdvisorImportClaimStatus.NotFound && claimStatus !== ScoClarityAdvisorImportClaimStatus.NotExpert && claimStatus !== ScoClarityAdvisorImportClaimStatus.ClaimedByMe;
  const proof: ClarityAdvisorImportProof = {
    blockedMessage,
    hint: canConfirm && proofMethod === ScoClarityLinkProofMethod.EmailCode ? "To confirm it's you, we'll email a code to the address on this Clarity profile." : null,
    codeStep: codeSent ? {
      maskedEmail: codeSent.maskedEmail,
      code,
      onCodeChange: setCode,
      onVerify: () => void runClaim(code),
      onResend: () => void sendCode(),
      verifying: holdPhase !== 'idle' || claimMutation.loading,
      resending: requestCodeMutation.loading,
      verifyLabel: 'Verify and continue'
    } : null
  };
  return {
    handle,
    applyHref,
    becomeHref,
    loginHref,
    importHref,
    importCtaLabel,
    hasImportableExpert,
    displayName,
    firstName,
    expert,
    claimStatus,
    canonicalScreenName,
    isRegistered,
    loading,
    queryError: importQuery.error,
    error: error || (importQuery.error && !preview ? prettyErrorMessage(importQuery.error) : null),
    holdPhase,
    claiming: holdPhase === 'claiming' || claimMutation.loading,
    sendingCode: requestCodeMutation.loading,
    proof,
    extracting: holdPhase === 'extracting' || holdPhase === 'saving' || holdPhase === 'redirecting' || extractMutation.loading,
    onConfirm: canConfirm ? onConfirm : undefined
  };
};